import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, App, AutoComplete, Button, DatePicker, Descriptions, Drawer, Dropdown, Empty, Form, Grid, Input, InputNumber, Modal,
  Popconfirm,
  Popover, Radio, Result, Segmented, Select, Space, Spin, Switch, Table, Tabs, Tooltip, Typography, theme,
} from 'antd';
import {
  AppstoreOutlined, CalendarOutlined, CheckCircleOutlined, CloudServerOutlined, DeleteOutlined, EditOutlined,
  ExclamationCircleOutlined, FieldTimeOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, GlobalOutlined,
  HistoryOutlined, PlusOutlined, PrinterOutlined, QuestionCircleOutlined, SyncOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import '../ortak/nfmodules.css';

/**
 * BT — Domain ve Hosting yenileme takibi (30.10.2026). Yalnız Bilgi Teknolojileri birimi (backend
 * routes/bt_yenileme.py BT pozisyonlarına göre denetler; 403 → erişim yok ekranı).
 * Kayıtlar: ne zaman alındı, son yenileme, sonraki yenileme ve kalan gün, beklenen tutar; ayrıntıda hangi yıl hangi
 * fiyattan yenilendiği (yıllık eşdeğer + değişim). Yıllık Ödeme Planı: önümüzdeki 12 ay ya da seçilen yıl, ay ay.
 * Hatırlatmalar (60 / 30 / 7 gün kala ve son gün) workers/bt_yenileme_worker.py — Fedora bildirimi, e-posta, SMS;
 * BT personeline ve bilgi için CFO ile Genel Koordinatör'e.
 * Rapor Al (02.11.2026): Kayıtlar sekmesinde ekrandaki süzgeç + seçili ödeme planı dönemiyle; Yıllık Ödeme Planı
 * sekmesinde ekrandaki plan gibi (ay kartları / kayıt × ay tablosu). Excel / PDF; Yazdır aynı PDF'i yazdırır
 * (services/bt_yenileme_rapor.py).
 * Yazı biçimi harcırahtaki gibi: düğme, etiket, sütun ve başlıklarda her kelimenin baş harfi büyük.
 */
const { Text, Title } = Typography;

const SURELER = [
  { value: 1, label: '1 Ay' }, { value: 3, label: '3 Ay' }, { value: 6, label: '6 Ay' }, { value: 12, label: '1 Yıl' },
  { value: 24, label: '2 Yıl' }, { value: 36, label: '3 Yıl' }, { value: 60, label: '5 Yıl' }, { value: 120, label: '10 Yıl' },
];
const sureAdi = (ay) => SURELER.find((s) => s.value === ay)?.label || `${ay} Ay`;
const SAGLAYICI_ONERILERI = ['İHS', 'Natro', 'GoDaddy', 'Turhost', 'Hostinger', 'Cloudflare'];
const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const tarih = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—');
const para = (t, pb) => (t === null || t === undefined ? '—'
  : new Intl.NumberFormat('tr-TR', { style: 'currency', currency: pb || 'TRY', maximumFractionDigits: 2 }).format(t));
const hata = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : e?.message || 'İşlem başarısız.';
};

// Kalan gün → renk ailesi (antd token: colorError / colorWarning / colorPrimary / colorSuccess; boş = nötr)
const SEVIYE_TON = { gecti: 'Error', kritik: 'Error', yakin: 'Warning', yaklasan: 'Primary', normal: 'Success', birakildi: '' };
const kalanAdi = (k) => (k < 0 ? `${-k} Gün Geçti` : k === 0 ? 'Bugün Son Gün' : `${k} Gün`);

const SEKMELER = [
  { k: 'gecti', ad: 'Süresi Geçen', alt: 'Yenilenmemiş', ton: 'Error', ikon: <ExclamationCircleOutlined /> },
  { k: 'gun30', ad: '30 Gün İçinde', alt: 'Yakında Yenilenecek', ton: 'Warning', ikon: <FieldTimeOutlined /> },
  { k: 'gun60', ad: '60 Gün İçinde', alt: 'Yaklaşan Yenilemeler', ton: 'Primary', ikon: <CalendarOutlined /> },
  { k: 'tumu', ad: 'Tümü', alt: '', ikon: <AppstoreOutlined /> },
];
const sekmeyeUyar = (k, s) => (s === 'tumu' ? true : k.durum !== 'aktif' ? false
  : s === 'gecti' ? k.kalan_gun < 0 : s === 'gun30' ? k.kalan_gun >= 0 && k.kalan_gun <= 30 : k.kalan_gun >= 0 && k.kalan_gun <= 60);

function useBtApi() {
  const { getAuthHeaders, token } = useAuth();
  return useMemo(() => {
    const cfg = () => ({ headers: getAuthHeaders() });
    const root = `${API_BASE_URL}/bt-yenileme`;
    const veri = (r) => r.data.data;
    return {
      get: (yol, params = {}) => axios.get(`${root}${yol}`, { ...cfg(), params }).then(veri),
      post: (yol, govde) => axios.post(`${root}${yol}`, govde, cfg()).then(veri),
      put: (yol, govde) => axios.put(`${root}${yol}`, govde, cfg()).then(veri),
      del: (yol) => axios.delete(`${root}${yol}`, cfg()).then(veri),
      dosya: (yol, params) => axios.get(`${root}${yol}`, { ...cfg(), params, responseType: 'blob' }),
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
}

function useTon() {
  const { token } = theme.useToken();
  return useCallback((ton) => (ton ? {
    '--t': token[`color${ton}`], '--t-sayi': token[`color${ton}`], '--t-zemin': token[`color${ton}Bg`],
    '--t-zemin2': token[`color${ton}BgHover`], '--t-kenar': token[`color${ton}Border`],
  } : {
    '--t': token.colorTextSecondary, '--t-sayi': token.colorText, '--t-zemin': token.colorFillQuaternary,
    '--t-zemin2': token.colorFillSecondary, '--t-kenar': token.colorBorder,
  }), [token]);
}

/** Renkli rozet: antd Tag yerine (koyu temada DarkMode.css Tag renklerini griye çeviriyor). */
function Rozet({ ton, children, ikon }) {
  const tonVars = useTon();
  return <span className="bt-rozet" style={tonVars(ton)}>{ikon}{children}</span>;
}

function KalanRozeti({ k }) {
  if (k.durum !== 'aktif') return <Rozet ton="">Bırakıldı</Rozet>;
  return <Rozet ton={SEVIYE_TON[k.seviye]}>{kalanAdi(k.kalan_gun)}</Rozet>;
}

const TurSimgesi = ({ tur }) => (tur === 'hosting' ? <CloudServerOutlined /> : <GlobalOutlined />);

// --- Rapor: dosya indir / PDF yazdır ----------------------------------------
const blobHatasi = async (e) => {
  try { return JSON.parse(await e.response.data.text()).detail || hata(e); } catch { return hata(e); } // blob değilse
};

function dosyayiIndir(r, yedekAd) {
  const ad = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || yedekAd;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = ad;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** PDF'i görünmez çerçevede açıp yazdırma penceresini getirir. pencere verilmişse (telefon: çerçeveden yazdırılamıyor)
 * PDF orada açılır, yazdırma tarayıcının kendi düğmesiyle. Çerçeve bir sonraki yazdırmaya kadar durur. */
function pdfYazdir(blob, pencere) {
  const url = URL.createObjectURL(blob);
  if (pencere) {
    pencere.location.href = url;
    return;
  }
  const eski = document.getElementById('bt-yazdir');
  if (eski) {
    URL.revokeObjectURL(eski.dataset.url);
    eski.remove();
  }
  const f = document.createElement('iframe');
  f.id = 'bt-yazdir';
  f.title = 'Yazdır';
  f.dataset.url = url;
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  f.onload = () => {
    try {
      f.contentWindow.focus();
      f.contentWindow.print();
    } catch {
      window.open(url, '_blank');
    }
  };
  f.src = url;
  document.body.appendChild(f);
}

// --- Kayıt formu ------------------------------------------------------------
function KayitPenceresi({ acik, kayit, secenekler, onKapat, onKaydet }) {
  const [form] = Form.useForm();
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const tur = Form.useWatch('tur', form) || 'domain';
  useEffect(() => {
    if (!acik) return;
    form.resetFields();
    form.setFieldsValue(kayit ? {
      ...kayit,
      alinma_tarihi: kayit.alinma_tarihi ? dayjs(kayit.alinma_tarihi) : null,
      bitis_tarihi: dayjs(kayit.bitis_tarihi),
    } : { tur: 'domain', periyot_ay: 12, para_birimi: 'TRY', otomatik_yenileme: false, durum: 'aktif' });
  }, [acik, kayit, form]);

  const saglayicilar = useMemo(() => [...new Set([...(secenekler?.saglayicilar || []), ...SAGLAYICI_ONERILERI])]
    .map((s) => ({ value: s })), [secenekler]);

  const kaydet = () => form.validateFields().then((v) => {
    setKaydediliyor(true);
    return onKaydet({
      ...v,
      alinma_tarihi: v.alinma_tarihi ? v.alinma_tarihi.format('YYYY-MM-DD') : null,
      bitis_tarihi: v.bitis_tarihi.format('YYYY-MM-DD'),
      tutar: v.tutar ?? null,
    }).finally(() => setKaydediliyor(false));
  }).catch(() => {}); // doğrulama / sunucu hatası alanda ve mesajda gösterildi

  return (
    <Modal open={acik} title={kayit ? 'Kaydı Düzenle' : 'Yeni Domain / Hosting'} onCancel={onKapat} onOk={kaydet}
      okText="Kaydet" cancelText="Vazgeç" confirmLoading={kaydediliyor} destroyOnHidden width={640} rootClassName="bt-modal">
      <Form form={form} layout="vertical">
        <Form.Item name="tur" label="Tür">
          <Segmented options={(secenekler?.turler || []).map((t) => ({
            value: t.value, label: <span><TurSimgesi tur={t.value} /> {t.label}</span> }))} />
        </Form.Item>
        <Form.Item name="ad" label={tur === 'domain' ? 'Alan Adı' : 'Paket Adı'} rules={[{ required: true, message: 'Zorunlu.' }]}>
          <Input placeholder={tur === 'domain' ? 'atmturkiye.com' : 'Linux Pro Hosting'} autoComplete="off" />
        </Form.Item>
        <div className="bt-form-ikili">
          <Form.Item name="saglayici" label="Sağlayıcı">
            <AutoComplete options={saglayicilar} placeholder="İHS, Natro…" filterOption={(g, o) =>
              o.value.toLocaleLowerCase('tr').includes((g || '').toLocaleLowerCase('tr'))} />
          </Form.Item>
          <Form.Item name="firma" label="Şirket">
            <Select allowClear showSearch optionFilterProp="label" options={secenekler?.firmalar || []} placeholder="Hangi Şirketimiz Adına" />
          </Form.Item>
        </div>
        {tur === 'hosting' && (
          <Form.Item name="bagli_alanlar" label="Üzerindeki Alan Adları">
            <Input placeholder="atmturkiye.com, atmpoint.com.tr" />
          </Form.Item>
        )}
        <div className="bt-form-ikili">
          <Form.Item name="alinma_tarihi" label="Alınma Tarihi">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} placeholder="İlk Alındığı Gün" />
          </Form.Item>
          <Form.Item name="bitis_tarihi" label="Sonraki Yenileme (Bitiş)" rules={[{ required: true, message: 'Zorunlu.' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </div>
        <div className="bt-form-ikili">
          <Form.Item name="periyot_ay" label="Yenileme Süresi">
            <Select options={SURELER} />
          </Form.Item>
          <Form.Item name="otomatik_yenileme" label="Otomatik Yenileme" valuePropName="checked">
            <Switch checkedChildren="Açık" unCheckedChildren="Kapalı" />
          </Form.Item>
        </div>
        <div className="bt-form-ikili">
          <Form.Item name="tutar" label="Beklenen Yenileme Tutarı (KDV Dahil)">
            <InputNumber min={0} style={{ width: '100%' }} decimalSeparator="," precision={2} placeholder="0,00" />
          </Form.Item>
          <Form.Item name="para_birimi" label="Para Birimi">
            <Select options={(secenekler?.para_birimleri || ['TRY']).map((p) => ({ value: p, label: p }))} />
          </Form.Item>
        </div>
        {kayit && (
          <Form.Item name="durum" label="Durum" extra="Bırakılan kayıt için hatırlatma gitmez, ödeme planına girmez.">
            <Radio.Group optionType="button" options={secenekler?.durumlar || []} />
          </Form.Item>
        )}
        <Form.Item name="notlar" label="Notlar" extra="Panel şifrelerini buraya yazmayın.">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// --- Yenileme formu ---------------------------------------------------------
function YenilemePenceresi({ durum, secenekler, onKapat, onKaydet }) {
  const [form] = Form.useForm();
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const kayit = durum?.kayit;
  const gecmis = durum?.gecmis;
  const sure = Form.useWatch('sure_ay', form);
  useEffect(() => {
    if (!kayit) return;
    form.resetFields();
    form.setFieldsValue({
      tarih: gecmis ? null : dayjs(), sure_ay: kayit.periyot_ay, tutar: kayit.tutar, para_birimi: kayit.para_birimi,
    });
  }, [kayit, gecmis, form]);
  if (!kayit) return null;
  const yeniBitis = sure ? dayjs(kayit.bitis_tarihi).add(sure, 'month') : null;

  const kaydet = () => form.validateFields().then((v) => {
    setKaydediliyor(true);
    return onKaydet(kayit, { ...v, tarih: v.tarih.format('YYYY-MM-DD'), tutar: v.tutar ?? null, ileri_al: !gecmis })
      .finally(() => setKaydediliyor(false));
  }).catch(() => {});

  return (
    <Modal open title={gecmis ? `${kayit.ad} · Geçmiş Yenileme Ekle` : `${kayit.ad} · Yenilendi`} onCancel={onKapat}
      onOk={kaydet} okText="Kaydet" cancelText="Vazgeç" confirmLoading={kaydediliyor} destroyOnHidden rootClassName="bt-modal">
      {gecmis
        ? <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Eski yılların yenilemesi yalnız fiyat geçmişine eklenir; sonraki yenileme tarihi değişmez." />
        : <Alert type="success" showIcon style={{ marginBottom: 12 }}
            message={<>Sonraki yenileme <b>{tarih(kayit.bitis_tarihi)}</b> → <b>{yeniBitis ? tarih(yeniBitis) : '—'}</b> olacak;
              hatırlatmalar yeni tarihe göre yeniden başlar.</>} />}
      <Form form={form} layout="vertical">
        <div className="bt-form-ikili">
          <Form.Item name="tarih" label="Yenileme Tarihi" rules={[{ required: true, message: 'Zorunlu.' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="sure_ay" label="Süre">
            <Select options={SURELER} />
          </Form.Item>
        </div>
        <div className="bt-form-ikili">
          <Form.Item name="tutar" label="Ödenen Tutar (KDV Dahil)">
            <InputNumber min={0} style={{ width: '100%' }} decimalSeparator="," precision={2} placeholder="0,00" />
          </Form.Item>
          <Form.Item name="para_birimi" label="Para Birimi">
            <Select options={(secenekler?.para_birimleri || ['TRY']).map((p) => ({ value: p, label: p }))} />
          </Form.Item>
        </div>
        <Form.Item name="notu" label="Not">
          <Input placeholder="Fatura no, kampanya, vb." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// --- Ayrıntı çekmecesi ------------------------------------------------------
function Ayrinti({ kayit, onKapat, onYenile, onGecmis, onDuzenle, onSil, onYenilemeSil }) {
  const dar = !Grid.useBreakpoint().md;
  if (!kayit) return null;
  const gecmis = [...(kayit.yenilemeler || [])].reverse();
  return (
    <Drawer open title={<span><TurSimgesi tur={kayit.tur} /> {kayit.ad}</span>} onClose={onKapat} width={dar ? '100%' : 720}
      rootClassName="bt-modal" extra={<KalanRozeti k={kayit} />}
      footer={(
        <Space wrap>
          {kayit.durum === 'aktif' && <Button type="primary" icon={<SyncOutlined />} onClick={() => onYenile(kayit)}>Yenilendi</Button>}
          <Button icon={<HistoryOutlined />} onClick={() => onGecmis(kayit)}>Geçmiş Yenileme Ekle</Button>
          <Button icon={<EditOutlined />} onClick={() => onDuzenle(kayit)}>Düzenle</Button>
          <Popconfirm title="Kayıt silinsin mi?" description="Hatırlatmalar da durur." okText="Sil" cancelText="Vazgeç"
            okButtonProps={{ danger: true }} onConfirm={() => onSil(kayit)}>
            <Button danger icon={<DeleteOutlined />}>Sil</Button>
          </Popconfirm>
        </Space>
      )}>
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered items={[
        { label: 'Tür', children: kayit.tur_adi },
        { label: 'Sağlayıcı', children: kayit.saglayici || '—' },
        { label: 'Şirket', children: kayit.firma_adi || '—' },
        { label: 'Durum', children: kayit.durum_adi },
        ...(kayit.tur === 'hosting' ? [{ label: 'Üzerindeki Alan Adları', span: 2, children: kayit.bagli_alanlar || '—' }] : []),
        { label: 'Alınma Tarihi', children: tarih(kayit.alinma_tarihi) },
        { label: 'Son Yenileme', children: tarih(kayit.son_yenileme) },
        { label: 'Sonraki Yenileme', children: <b>{tarih(kayit.bitis_tarihi)}</b> },
        { label: 'Yenileme Süresi', children: sureAdi(kayit.periyot_ay) },
        { label: 'Beklenen Tutar', children: para(kayit.tutar, kayit.para_birimi) },
        { label: 'Otomatik Yenileme', children: kayit.otomatik_yenileme ? 'Açık' : 'Kapalı' },
        ...(kayit.notlar ? [{ label: 'Notlar', span: 2, children: <span style={{ whiteSpace: 'pre-wrap' }}>{kayit.notlar}</span> }] : []),
      ]} />
      <Title level={5} style={{ marginTop: 20 }}>Yenileme Geçmişi</Title>
      <Table size="small" rowKey="id" pagination={false} dataSource={gecmis} scroll={dar ? undefined : { x: 640 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Henüz yenileme girilmedi." /> }}
        columns={[
          { title: 'Yıl', dataIndex: 'yil', width: 60, responsive: ['md'] },
          { title: 'Tarih', dataIndex: 'tarih', render: tarih, width: 100 },
          { title: 'Süre', dataIndex: 'sure_ay', render: sureAdi, width: 70, responsive: ['sm'] },
          { title: 'Tutar', dataIndex: 'tutar', align: 'right', render: (v, y) => para(v, y.para_birimi) },
          // Yıllık eşdeğer yalnız 1 yıldan farklı süreli yenileme varsa (2 yıllık 1.000 ₺ = yıllık 500 ₺)
          ...(gecmis.some((y) => y.sure_ay !== 12) ? [{ title: 'Yıllık', dataIndex: 'yillik_tutar', align: 'right',
            render: (v, y) => (y.sure_ay === 12 ? <Text type="secondary">—</Text> : para(v, y.para_birimi)) }] : []),
          { title: 'Değişim', dataIndex: 'degisim_yuzde', align: 'right', width: 90,
            render: (v) => (v === null || v === undefined ? '—'
              : <Rozet ton={v > 0 ? 'Warning' : v < 0 ? 'Success' : ''}>{v > 0 ? '+' : ''}{v.toLocaleString('tr-TR')}%</Rozet>) },
          { title: 'Not', dataIndex: 'notu', responsive: ['md'], render: (v, y) => (
            <span>{v || ''}{!y.yeni_bitis && <Text type="secondary" style={{ fontSize: 12 }}>{v ? ' · ' : ''}Geçmiş Kaydı</Text>}</span>) },
          { title: '', key: 'sil', width: 40, render: (_, y) => (
            <Popconfirm title="Bu yenileme silinsin mi?" okText="Sil" cancelText="Vazgeç" okButtonProps={{ danger: true }}
              description={y.yeni_bitis ? `Sonraki yenileme ${tarih(y.onceki_bitis)} tarihine geri alınır.` : null}
              onConfirm={() => onYenilemeSil(kayit, y)}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Yenilemeyi sil" />
            </Popconfirm>) },
        ]} />
    </Drawer>
  );
}

// --- Yıllık ödeme planı -----------------------------------------------------
function OdemePlani({ api, onAc, secim, setSecim }) {
  const buYil = dayjs().year();
  const [plan, setPlan] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const { message } = App.useApp();
  useEffect(() => {
    setYukleniyor(true);
    api.get('/odeme-plani', secim === '12ay' ? {} : { yil: secim }).then(setPlan)
      .catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, secim, message]);

  const ayAdi = (a) => `${AYLAR[Number(a.slice(5, 7)) - 1]} ${a.slice(0, 4)}`;
  const tutarVar = (a) => a.odemeler.some((o) => o.tutar !== null && o.tutar !== undefined);
  // Genel toplam: planda yer alan her kayıt bir kez, son yenilemede ödenen tutar (yoksa beklenen) — ay toplamları ayrı
  const son = plan?.son_toplam;
  const dovizli = son && Object.keys(son.toplamlar).some((p) => p !== 'TRY');
  return (
    <div>
      <div className="bt-plan-ust">
        <Segmented value={secim} onChange={setSecim} options={[
          { value: '12ay', label: 'Önümüzdeki 12 Ay' },
          { value: buYil, label: String(buYil) }, { value: buYil + 1, label: String(buYil + 1) },
        ]} />
        {son && (
          <div className="bt-plan-toplam">
            <Tooltip title={<>Plandaki her kayıt bir kez: son yenilemede ödenen tutar; yenileme girilmemişse kayıttaki
              beklenen tutar. Ay kartlarındaki toplamlar o ayın planlanan ödemeleridir.</>}>
              <Text type="secondary" style={{ cursor: 'help' }}>Son Yenileme Toplamı <QuestionCircleOutlined /></Text>
            </Tooltip>
            <b>{para(son.toplam_tl, 'TRY')}</b>
            {dovizli && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {Object.entries(son.toplamlar).map(([pb, t]) => para(t, pb)).join(' + ')}
                {plan.kur?.tarih && ` · TL karşılığı ${tarih(plan.kur.tarih)} TCMB kuruyla`}
              </Text>
            )}
          </div>
        )}
      </div>
      {son?.tutarsiz > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`${son.tutarsiz} kaydın tutarı girilmemiş; toplama dahil değil.`} />
      )}
      {yukleniyor && !plan ? <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div> : plan && (
        <Spin spinning={yukleniyor}>
          <div className="bt-aylar">
            {plan.aylar.map((a) => (
              <div key={a.ay} className={`bt-ay${a.odemeler.length ? '' : ' bos'}`}>
                <div className="bt-ay-bas">
                  <span className="bt-ay-ad">{ayAdi(a.ay)}</span>
                  <span className={`bt-ay-tutar${tutarVar(a) ? '' : ' soluk'}`}>
                    {!a.odemeler.length ? 'Ödeme Yok' : tutarVar(a) ? para(a.toplam_tl, 'TRY') : 'Tutar Girilmemiş'}
                  </span>
                </div>
                {a.odemeler.map((o) => (
                  <button key={`${o.id}-${o.tarih}`} type="button" className="bt-odeme" onClick={() => onAc(o.id)}>
                    <span className="bt-odeme-gun">{o.gecikmis ? <Rozet ton="Error">Gecikmiş</Rozet> : dayjs(o.tarih).format('DD')}</span>
                    <span className="bt-odeme-ad"><TurSimgesi tur={o.tur} /> {o.ad}
                      {o.otomatik_yenileme && <Text type="secondary" style={{ fontSize: 11 }}> · Otomatik</Text>}</span>
                    <span className="bt-odeme-tutar">
                      {para(o.tutar, o.para_birimi)}
                      {o.para_birimi !== 'TRY' && o.tl !== null && <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>≈ {para(o.tl, 'TRY')}</Text>}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </Spin>
      )}
    </div>
  );
}

// --- Sayfa ------------------------------------------------------------------
export default function DomainHostingPage() {
  const api = useBtApi();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const tonVars = useTon();
  const dar = !Grid.useBreakpoint().md; // telefonda tablo yana kaymasın: Kayıt + Kalan iki sütun
  const [veri, setVeri] = useState(null);
  const [erisimHatasi, setErisimHatasi] = useState(null);
  const [suzgec, setSuzgec] = useState('tumu');
  const [tur, setTur] = useState('hepsi');
  const [ara, setAra] = useState('');
  const [sekme, setSekme] = useState('kayitlar');
  const [form, setForm] = useState({ acik: false, kayit: null });
  const [yenileme, setYenileme] = useState(null);
  const [ayrintiId, setAyrintiId] = useState(null);
  const [planSecim, setPlanSecim] = useState('12ay'); // Ödeme Planı sekmesindeki dönem; rapor da bunu kullanır
  const [raporBekliyor, setRaporBekliyor] = useState(false);

  const yukle = useCallback(() => api.get('').then(setVeri).catch((e) => {
    if (e?.response?.status === 403) setErisimHatasi(hata(e));
    else message.error(hata(e));
  }), [api, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const kayitlar = veri?.kayitlar || [];
  const sayi = useMemo(() => Object.fromEntries(SEKMELER.map((s) => [s.k, kayitlar.filter((k) => sekmeyeUyar(k, s.k)).length])), [kayitlar]);
  const liste = useMemo(() => {
    const a = ara.trim().toLocaleLowerCase('tr');
    return kayitlar.filter((k) => sekmeyeUyar(k, suzgec) && (tur === 'hepsi' || k.tur === tur)
      && (!a || [k.ad, k.saglayici, k.firma_adi, k.bagli_alanlar].some((v) => (v || '').toLocaleLowerCase('tr').includes(a))))
      .sort((x, y) => (x.durum !== y.durum ? (x.durum === 'aktif' ? -1 : 1) : x.kalan_gun - y.kalan_gun));
  }, [kayitlar, suzgec, tur, ara]);
  const ayrinti = kayitlar.find((k) => k.id === ayrintiId) || null;

  if (erisimHatasi) return <Result status="403" title="Erişim Yok" subTitle={erisimHatasi} />;
  if (!veri) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;

  const islem = (p, basari) => p.then((d) => { message.success(basari); yukle(); return d; })
    .catch((e) => { message.error(hata(e)); throw e; });
  const kaydet = (govde) => islem(form.kayit ? api.put(`/${form.kayit.id}`, govde) : api.post('', govde),
    form.kayit ? 'Kayıt güncellendi.' : 'Kayıt eklendi.').then(() => setForm({ acik: false, kayit: null }));
  const yenilemeKaydet = (kayit, govde) => islem(api.post(`/${kayit.id}/yenileme`, govde),
    govde.ileri_al ? 'Yenileme kaydedildi.' : 'Geçmişe eklendi.').then(() => setYenileme(null));
  const sil = (kayit) => islem(api.del(`/${kayit.id}`), 'Kayıt silindi.').then(() => setAyrintiId(null)).catch(() => {});
  const yenilemeSil = (kayit, y) => islem(api.del(`/${kayit.id}/yenileme/${y.id}`), 'Yenileme silindi.').catch(() => {});
  // Kayıtlar sekmesinde ekrandaki süzgeçle; Ödeme Planı sekmesinde ekrandaki plan gibi (süzgeçsiz)
  const planRaporu = sekme === 'plan';
  const raporAl = (bicim) => {
    const yazdir = bicim === 'yazdir';
    const pencere = yazdir && dar ? window.open('', '_blank') : null; // tıklama anında aç: sonradan açılan engellenir
    const kapat = message.loading(yazdir ? 'Yazdırma hazırlanıyor…' : 'Rapor hazırlanıyor…', 0);
    setRaporBekliyor(true);
    api.dosya('/rapor', {
      bicim: yazdir ? 'pdf' : bicim, kapsam: planRaporu ? 'plan' : 'kayitlar',
      ...(planRaporu ? {} : { suzgec, tur, ara: ara.trim() || undefined }),
      yil: planSecim === '12ay' ? undefined : planSecim,
    }).then((r) => (yazdir ? pdfYazdir(r.data, pencere) : dosyayiIndir(r, `domain-hosting-raporu.${bicim}`)))
      .catch(async (e) => { pencere?.close(); message.error(await blobHatasi(e)); })
      .finally(() => { kapat(); setRaporBekliyor(false); });
  };
  const planAdi = planSecim === '12ay' ? 'Önümüzdeki 12 Ay' : `${planSecim} Yılı`;

  const tumuAlt = `${veri.ozet.domain} Domain · ${veri.ozet.hosting} Hosting`;
  const nasil = (
    <ul style={{ margin: 0, paddingLeft: 18, maxWidth: 380, fontSize: 13 }}>
      <li>Sonraki yenileme tarihine 60, 30 ve 7 gün kala ve son gün BT personeline Fedora bildirimi, e-posta ve SMS gider; CFO ve Genel Koordinatör de bilgi için alır.</li>
      <li>Yenileme yapınca <b>Yenilendi</b>'ye basın: sonraki tarih süre kadar ileri alınır, hatırlatmalar yeni tarihe göre başlar.</li>
      <li>Eski yılların fiyatlarını <b>Geçmiş Yenileme Ekle</b> ile girebilirsiniz; tarih değişmez.</li>
      <li>Yıllık ödeme planı, kayıtların sonraki yenileme tarihi ve beklenen tutarından hesaplanır. <b>Son Yenileme
        Toplamı</b> plandaki her kaydı bir kez sayar: son yenilemede ödenen tutar, yenileme girilmemişse beklenen tutar.</li>
      <li><b>Rapor Al</b> Kayıtlar sekmesinde ekranda süzülen kayıtları, Yıllık Ödeme Planı sekmesinde ise seçili dönemin
        planını ekrandaki gibi ay ay Excel ya da PDF olarak verir; <b>Yazdır</b> aynı PDF'i yazdırır.</li>
    </ul>
  );

  const kayitHucresi = (v, k) => (
    <div className="bt-kayit">
      <span className="bt-kayit-ikon"><TurSimgesi tur={k.tur} /></span>
      <div>
        <div className="bt-kayit-ad">{v}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>{[k.tur_adi, k.saglayici].filter(Boolean).join(' · ')}</Text>
        {dar && <Text style={{ fontSize: 12, display: 'block' }}>Sonraki: <b>{tarih(k.bitis_tarihi)}</b></Text>}
      </div>
    </div>);
  const sutunlar = dar ? [
    { title: 'Kayıt', dataIndex: 'ad', render: kayitHucresi },
    { title: 'Kalan', dataIndex: 'kalan_gun', align: 'right', render: (_, k) => (
      <div><KalanRozeti k={k} /><Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>{para(k.tutar, k.para_birimi)}</Text></div>) },
  ] : [
    { title: 'Kayıt', dataIndex: 'ad', render: kayitHucresi },
    { title: 'Şirket', dataIndex: 'firma_adi', render: (v) => v || '—', responsive: ['md'] },
    { title: 'Alınma', dataIndex: 'alinma_tarihi', render: tarih, width: 100, responsive: ['lg'] },
    { title: 'Son Yenileme', dataIndex: 'son_yenileme', width: 120, responsive: ['md'], render: (v, k) => {
      const son = k.yenilemeler?.filter((y) => y.tarih === v).slice(-1)[0];
      return <div>{tarih(v)}{son?.tutar !== null && son?.tutar !== undefined && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{para(son.tutar, son.para_birimi)}</Text>)}</div>;
    } },
    { title: 'Sonraki Yenileme', dataIndex: 'bitis_tarihi', width: 140, render: (v, k) => (
      <div><b>{tarih(v)}</b>{k.otomatik_yenileme && <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Otomatik Yenileme</Text>}</div>) },
    { title: 'Kalan', dataIndex: 'kalan_gun', width: 130, render: (_, k) => <KalanRozeti k={k} /> },
    { title: 'Yenileme Tutarı', dataIndex: 'tutar', align: 'right', width: 140, render: (v, k) => (
      <div>{para(v, k.para_birimi)}<Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{sureAdi(k.periyot_ay)}</Text></div>) },
    { title: '', key: 'eylem', width: 120, render: (_, k) => k.durum === 'aktif' && (
      <Button size="small" icon={<CheckCircleOutlined />} onClick={(e) => { e.stopPropagation(); setYenileme({ kayit: k }); }}>
        Yenilendi
      </Button>) },
  ];

  const sayfaVars = {
    '--bt-yuzey': token.colorBgContainer, '--bt-kenar': token.colorBorderSecondary, '--bt-yazi': token.colorText,
    '--bt-ikincil': token.colorTextSecondary, '--bt-soluk': token.colorTextQuaternary, '--bt-dolgu': token.colorFillQuaternary,
    '--bt-golge': token.boxShadowTertiary, '--bt-birincil': token.colorPrimary,
  };

  return (
    <div className="nf-module bt-sayfa" style={sayfaVars}>
      <style>{`
        body[data-theme='dark'] :is(.bt-sayfa, .bt-modal) .ant-alert-warning { background-color: rgba(250, 173, 20, 0.12) !important; border-color: #d89614 !important; }
        body[data-theme='dark'] :is(.bt-sayfa, .bt-modal) .ant-alert-success { background-color: rgba(82, 196, 26, 0.1) !important; border-color: #49aa19 !important; }
        body[data-theme='dark'] :is(.bt-sayfa, .bt-modal) .ant-alert-info { background-color: rgba(24, 144, 255, 0.1) !important; border-color: #177ddc !important; }
        body[data-theme='dark'] :is(.bt-sayfa, .bt-modal) .ant-btn-dangerous:not(.ant-btn-primary) { border-color: #ff4d4f !important; color: #ff7875 !important; }
        body[data-theme='dark'] :is(.bt-sayfa, .bt-modal) .ant-table-tbody > tr:nth-child(even) > td { background: rgba(255, 255, 255, 0.025) !important; }
        body[data-theme='dark'] :is(.bt-sayfa, .bt-modal) .ant-table-tbody > tr:nth-child(odd) > td { background: transparent !important; }
        body[data-theme='dark'] :is(.bt-sayfa, .bt-modal) .ant-table-tbody > tr:hover > td { background: rgba(22, 119, 255, 0.12) !important; }
        .bt-rozet { display: inline-flex; align-items: center; gap: 4px; padding: 1px 8px; border-radius: 999px; font-size: 12px;
                    font-weight: 600; line-height: 20px; white-space: nowrap; color: var(--t); background: var(--t-zemin);
                    border: 1px solid var(--t-kenar); }
        .bt-ust { display: flex; justify-content: space-between; align-items: center; gap: 8px 16px; flex-wrap: wrap; margin-bottom: 12px; }
        .bt-sekmeler { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
        .bt-sekme { position: relative; overflow: hidden; text-align: left; font: inherit; cursor: pointer; color: var(--bt-yazi);
                    background: var(--bt-yuzey); border: 1px solid var(--bt-kenar); border-radius: 12px; padding: 12px 14px 14px;
                    display: grid; grid-template-columns: auto minmax(0, 1fr) auto; grid-template-areas: "ad ad ikon" "sayi alt alt";
                    column-gap: 8px; row-gap: 2px; align-items: center; align-content: start;
                    transition: border-color .15s, box-shadow .15s, background-color .15s, transform .15s; }
        .bt-sekme::after { content: ''; position: absolute; left: 14px; right: 14px; bottom: 0; height: 3px; border-radius: 3px 3px 0 0;
                           background: var(--t); opacity: 0; transition: opacity .15s; }
        .bt-sekme:hover { border-color: var(--t-kenar); box-shadow: var(--bt-golge); transform: translateY(-1px); }
        .bt-sekme:focus-visible { outline: 2px solid var(--t); outline-offset: 2px; }
        .bt-sekme.secili { background: var(--t-zemin); border-color: var(--t-kenar); }
        .bt-sekme.secili::after { opacity: 1; }
        .bt-sekme-ikon { grid-area: ikon; width: 32px; height: 32px; border-radius: 10px; display: grid; place-items: center;
                         font-size: 16px; color: var(--t); background: var(--t-zemin); }
        .bt-sekme.secili .bt-sekme-ikon { background: var(--t-zemin2); }
        .bt-sekme.bos:not(.secili) .bt-sekme-ikon { color: var(--bt-soluk); background: var(--bt-dolgu); }
        .bt-sekme-ad { grid-area: ad; font-size: 13px; font-weight: 600; color: var(--bt-ikincil); }
        .bt-sekme.secili .bt-sekme-ad { color: var(--bt-yazi); }
        .bt-sekme-sayi { grid-area: sayi; align-self: baseline; font-size: 28px; font-weight: 700; line-height: 1.15;
                         font-variant-numeric: tabular-nums; }
        .bt-sekme.secili .bt-sekme-sayi, .bt-sekme.acil .bt-sekme-sayi { color: var(--t-sayi); }
        .bt-sekme.bos .bt-sekme-sayi { color: var(--bt-soluk); }
        .bt-sekme-alt { grid-area: alt; align-self: baseline; font-size: 12px; line-height: 1.3; color: var(--bt-ikincil); }
        @media (prefers-reduced-motion: reduce) { .bt-sekme, .bt-sekme::after { transition: none; } .bt-sekme:hover { transform: none; } }
        .bt-suzgec { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 10px; }
        .bt-suzgec .ant-input-search { max-width: 280px; }
        .bt-kayit { display: flex; gap: 10px; align-items: center; }
        .bt-kayit-ikon { width: 30px; height: 30px; flex: none; border-radius: 8px; display: grid; place-items: center;
                         background: var(--bt-dolgu); color: var(--bt-birincil); }
        .bt-kayit-ad { font-weight: 600; word-break: break-all; }
        .bt-sayfa .ant-table-tbody > tr { cursor: pointer; }
        .bt-form-ikili { display: grid; grid-template-columns: 1fr 1fr; gap: 0 12px; }
        .bt-plan-ust { display: flex; justify-content: space-between; align-items: center; gap: 8px 16px; flex-wrap: wrap; margin-bottom: 12px; }
        .bt-plan-toplam { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
        .bt-plan-toplam b { font-size: 20px; font-variant-numeric: tabular-nums; }
        .bt-aylar { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
        .bt-ay { border: 1px solid var(--bt-kenar); border-radius: 12px; background: var(--bt-yuzey); padding: 10px 12px; min-height: 84px; }
        .bt-ay.bos { background: transparent; }
        .bt-ay-bas { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; margin-bottom: 6px; }
        .bt-ay-ad { font-weight: 600; }
        .bt-ay-tutar { font-weight: 700; font-variant-numeric: tabular-nums; }
        .bt-ay.bos .bt-ay-tutar, .bt-ay.bos .bt-ay-ad { color: var(--bt-soluk); font-weight: 500; }
        .bt-ay-tutar.soluk { color: var(--bt-ikincil); font-weight: 500; font-size: 12px; }
        .bt-odeme { width: 100%; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 8px; align-items: center;
                    padding: 5px 6px; margin: 0 -6px; border: 0; border-radius: 8px; background: transparent; color: inherit;
                    font: inherit; font-size: 13px; text-align: left; cursor: pointer; }
        .bt-odeme:hover, .bt-odeme:focus-visible { background: var(--bt-dolgu); outline: none; }
        .bt-odeme-gun { min-width: 24px; font-weight: 600; color: var(--bt-ikincil); font-variant-numeric: tabular-nums; }
        .bt-odeme-ad { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .bt-odeme-tutar { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
        @media (max-width: 991px) { .bt-aylar { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px) {
          .bt-sekmeler { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
          .bt-sekme { grid-template-columns: auto minmax(0, 1fr); grid-template-areas: "ikon ad" "ikon sayi"; column-gap: 10px;
                      row-gap: 0; padding: 8px 10px 10px 8px; }
          .bt-sekme:hover { transform: none; }
          .bt-sekme-ikon { width: 36px; height: 36px; font-size: 17px; }
          .bt-sekme-ad { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .bt-sekme-sayi { align-self: start; font-size: 20px; line-height: 1.1; }
          .bt-sekme-alt { display: none; }
          .bt-aylar { grid-template-columns: 1fr; }
          .bt-form-ikili { grid-template-columns: 1fr; }
          .bt-suzgec .ant-input-search { max-width: none; flex: 1 1 100%; }
        }
      `}</style>
      <div className="bt-ust">
        <Title level={3} style={{ margin: 0 }}><GlobalOutlined /> Domain ve Hosting Takibi</Title>
        <Space wrap>
          <Popover title="Nasıl Çalışır?" content={nasil} trigger="click" placement="bottomRight">
            <Button type="text" icon={<QuestionCircleOutlined />}>Nasıl Çalışır?</Button>
          </Popover>
          <Dropdown trigger={['click']} disabled={raporBekliyor} menu={{
            items: [{ type: 'group', label: planRaporu ? `Yıllık Ödeme Planı · ${planAdi}`
              : `Ekrandaki ${liste.length} Kayıt · ${planAdi} Planı`, children: [
              { key: 'xlsx', icon: <FileExcelOutlined style={{ color: '#1d6f42' }} />, label: 'Excel İndir' },
              { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#c0392b' }} />, label: 'PDF İndir' },
              { key: 'yazdir', icon: <PrinterOutlined />, label: 'Yazdır' },
            ] }],
            onClick: ({ key }) => raporAl(key),
          }}>
            <Button icon={<FileTextOutlined />} loading={raporBekliyor}>Rapor Al</Button>
          </Dropdown>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setForm({ acik: true, kayit: null })}>Yeni Kayıt</Button>
        </Space>
      </div>
      <Tabs activeKey={sekme} onChange={setSekme} items={[
        { key: 'kayitlar', label: 'Kayıtlar', children: (
          <>
            <div className="bt-sekmeler" role="tablist" aria-label="Yenileme durumu">
              {SEKMELER.map((t) => (
                <button key={t.k} type="button" role="tab" aria-selected={suzgec === t.k} onClick={() => setSuzgec(t.k)}
                  style={tonVars(t.ton)}
                  className={`bt-sekme${suzgec === t.k ? ' secili' : ''}${t.k === 'gecti' && sayi.gecti ? ' acil' : ''}${sayi[t.k] ? '' : ' bos'}`}>
                  <span className="bt-sekme-ikon" aria-hidden>{t.ikon}</span>
                  <span className="bt-sekme-ad">{t.ad}</span>
                  <span className="bt-sekme-sayi">{sayi[t.k]}</span>
                  <span className="bt-sekme-alt">{t.k === 'tumu' ? tumuAlt : t.alt}</span>
                </button>
              ))}
            </div>
            <div className="bt-suzgec">
              <Segmented value={tur} onChange={setTur} options={[
                { value: 'hepsi', label: 'Hepsi' }, { value: 'domain', label: 'Domain' }, { value: 'hosting', label: 'Hosting' }]} />
              <Input.Search allowClear placeholder="Alan Adı, Sağlayıcı, Şirket" value={ara} onChange={(e) => setAra(e.target.value)} />
              {(suzgec !== 'tumu' || tur !== 'hepsi' || ara) && <Text type="secondary" style={{ fontSize: 13 }}>{liste.length} / {kayitlar.length} Kayıt</Text>}
            </div>
            <Table rowKey="id" size="middle" dataSource={liste} columns={sutunlar} pagination={liste.length > 50 ? { pageSize: 50 } : false}
              scroll={dar ? undefined : { x: 760 }} onRow={(k) => ({ onClick: () => setAyrintiId(k.id) })}
              locale={{ emptyText: (
                <Empty description={kayitlar.length ? 'Bu süzgeçte kayıt yok.' : 'Henüz kayıt yok. Domain ve hosting\'leri ekleyerek başlayın.'}>
                  {!kayitlar.length && <Button type="primary" icon={<PlusOutlined />} onClick={() => setForm({ acik: true, kayit: null })}>Yeni Kayıt</Button>}
                </Empty>) }} />
          </>) },
        { key: 'plan', label: 'Yıllık Ödeme Planı', children: (
          <OdemePlani api={api} onAc={setAyrintiId} secim={planSecim} setSecim={setPlanSecim} />) },
      ]} />
      <KayitPenceresi acik={form.acik} kayit={form.kayit} secenekler={veri.secenekler}
        onKapat={() => setForm({ acik: false, kayit: null })} onKaydet={kaydet} />
      <YenilemePenceresi durum={yenileme} secenekler={veri.secenekler} onKapat={() => setYenileme(null)} onKaydet={yenilemeKaydet} />
      <Ayrinti kayit={ayrinti} onKapat={() => setAyrintiId(null)}
        onYenile={(k) => setYenileme({ kayit: k })} onGecmis={(k) => setYenileme({ kayit: k, gecmis: true })}
        onDuzenle={(k) => setForm({ acik: true, kayit: k })} onSil={sil} onYenilemeSil={yenilemeSil} />
    </div>
  );
}
